import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleNewExperienceModal,
  createExperience,
} from 'data_system/resource_portal/actions/experienceActions';
import {
  getLinkColor,
  getExperienceUpdating,
  getShowNewExperienceModal,
} from 'data_system/resource_portal/selectors';
import { getUser } from 'data_system/token_auth/selectors';
import useWindowDimensions from 'ui_system/utilities/useWindowDimensions';
import BaseModal from 'ui_system/modals/BaseModal';
import { MainButton } from 'ui_system/modals/ConfirmationModal';
import { Theme } from 'ui_system/theme';
import { TourName } from 'views/anyguide/tours/create/TourName';
import { Icon as Icn } from 'views/anyguide/layout/uikit';
import { t } from 'services/translation';
import { lowercase } from 'lib/string_utils';
import styled from '@emotion/styled';
import RadioPair from 'ui_system/forms/RadioPair';
import { isProduction } from 'lib/environmentFlags';

const defaultColor = (color) => color || Theme.colors.green;

// XXX Copied from frontend/components/views/anyguide/tours/create/NewTourWizard.js
const BackButton = styled.span`
  color: #fff;
  cursor: pointer;
  display: inline-block;
  left: 15px;
  position: fixed;
  top: 21px;

  * {
    display: inline-block;
  }

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    color: ${Theme.colors.lightText};
    left: 0;
    position: absolute;
    top: 68%;
  }
  display: ${(props) => (props.step === 0 ? 'none' : 'inline-block')};
`;

// XXX Copied from frontend/components/views/anyguide/tours/create/NewTourWizard.js
const BackText = styled.span`
  bottom: 4px;
  color: ${Theme.colors.lightText};
  font-size: 1.6rem;
  left: 20px;
  position: absolute;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    &:after {
      content: 'Back';
    }
  }
`;

export const ConfirmButton = styled(MainButton)`
  font-size: 15px;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 18px;
  padding-right: 18px;
  border: 1px solid ${defaultColor((props) => props.brandColor)};
  width: 140px;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    font-size: 16px;
  }
`;

export const Modal = styled(BaseModal)`
  width: 100vw;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    width: 550px;
    max-height: 310px;
  }
`;

const Icon = styled(Icn)`
  fill: white;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    width: 550px;
    max-height: 310px;
  }
`;

export const ModalBody = styled.div`
  text-align: left;
  margin: 0px;
  height: 100vh;
  width: 100vw;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    display: block;
    padding: 30px 35px;
    width: 550px;
    max-height: 310px;
  }
`;

export const Title = styled.div`
  font-size: 17px;
  font-weight: 400;
  text-align: center;
  padding: 0px 48px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    div {
      width: 50vw;
      display: inline-block;
    }
  }
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    font-size: 18px;
    padding: initial;
    margin-bottom: 40px;
    margin-top: 10px;
    width: 100%;
  }
`;

const Strong = styled.span`
  font-weight: 600;
`;

const StepIndicatorContainer = styled.div`
  text-align: center;
  margin-bottom: 15px;
`;

const StepIndicator = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border: 1px solid ${Theme.colors.lightLine};
  background-color: ${(props) => props.backgroundColor};
  border-radius: 50%;
  margin: 0 5px;
`;

const Footer = styled.div`
  border-top: ${(props) =>
    props.topBorder ? `1px solid ${Theme.colors.lightLine}` : 'none'};
  padding-top: 10px;
  text-align: center;
  position: relative;
  margin: 0% 4.4%;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    width: 480px;
    margin: initial;
  }
`;

const RadioContainer = styled.div`
  margin-left: 48px;
  margin-top: 4.5%;
  margin-bottom: 40px;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    margin-bottom: 43px;
    margin-left: 100px;
    width: 290px;
  }
`;

const NameContainer = styled.div`
  margin-top: 4.5%;
  margin-bottom: 63px;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    margin-bottom: 41px;
  }
`;

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const IconContainer = styled.div`
  display: inline-block;
  span {
    color: white;
    font-size: 18px;
  }
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    font-size: 15px;
    span {
      color: ${Theme.colors.lightText};
    }
  }
`;

const MobileHeader = styled.div`
  background-color: ${Theme.colors.darkText};
  height: 60px;
  width: 100vw;
  margin-bottom: 30px;
  text-align: center;
`;

const MobileHeaderCenter = styled.div`
  color: white;
  padding-top: 20px;
  font-weight: 500;
`;

const NewExperienceModal = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const isOnlineExperiencesAllowed =
    user?.data?.attributes?.isOnlineExperiencesAllowed;
  const brandColor = useSelector(getLinkColor);
  const visible = useSelector(getShowNewExperienceModal);
  const creating = useSelector(getExperienceUpdating);

  // Paid/Free maps to a hardcoded `customTypeId` based on the `resource.user`
  // Currently only enabled for Michaels or Michaels Canada only.
  //
  // The backend uses `customTypeId` and `online` to select a template to
  // assign defaults in Api::CreateTourForm (also see Tour#template)
  //
  const resourceUserId = user?.data?.attributes?.userId;

  const MICHAELS_USER_ID = 42253;
  const MICHAELS_CANADA_USER_ID = 42446;

  const isExperienceTypeStepEnabled = [
    MICHAELS_USER_ID,
    MICHAELS_CANADA_USER_ID,
  ].includes(resourceUserId);

  let paidCustomTypeId;
  let freeCustomTypeId;

  if (isExperienceTypeStepEnabled) {
    switch (resourceUserId) {
      case MICHAELS_USER_ID: {
        paidCustomTypeId = 17;
        freeCustomTypeId = 213;
        break;
      }
      case MICHAELS_CANADA_USER_ID: {
        paidCustomTypeId = 11;
        freeCustomTypeId = 215;
        break;
      }
      default: // Use null for both Free/Paid
    }
  }

  const [values, setValues] = useState({
    name: '',
    customTypeId: paidCustomTypeId,
    online: isOnlineExperiencesAllowed,
  });
  const [step, setStep] = useState(0);
  const dimensions = useWindowDimensions();
  const isDesktop = dimensions.width >= Theme.mobile.phoneWidth;

  const moveToNextStep = () => {
    setStep(step + 1);
  };
  const toggleModal = () => {
    dispatch(toggleNewExperienceModal(false));
  };

  const moveToPreviousStep = () => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      toggleModal();
    }
  };

  const handleInputChange = (key, value) => {
    setValues({ ...values, [key]: value });
  };

  const handleRadioChange = (name, value) => {
    setValues({ ...values, [name]: value });
  };

  const handleCreateExperience = () => {
    dispatch(createExperience(user.data.id, values));
  };

  const steps = [];

  if (isExperienceTypeStepEnabled) {
    steps.push({
      title: (
        <div>
          {`${t('WHAT')} `} <Strong>{t('TYPE')}</Strong>{' '}
          {` ${t('OF_CLASS_WOULD_YOU_LIKE_TO_CREATE')}?`}
        </div>
      ),
      renderBodyFunc: () => {
        const experienceNameSingular = t('EXPERIENCE_NAME_SINGULAR');
        const i18nOpts = { experience_name: experienceNameSingular };
        const radioPairProps = {
          field: {
            name: 'customTypeId',
            value: values.customTypeId || paidCustomTypeId,
          },
          labels: [
            t('PAID_EXPERIENCE', `Paid ${experienceNameSingular}`, i18nOpts),
            t('FREE_EXPERIENCE', `Free ${experienceNameSingular}`, i18nOpts),
          ],
          values: [paidCustomTypeId, freeCustomTypeId],
          handleToggle: handleRadioChange,
          format: 'minimal',
          isCentered: isDesktop,
          isStacked: !isDesktop,
          color: brandColor,
          disabled: [false, false],
        };

        return (
          <RadioContainer>
            <RadioPair {...radioPairProps} />
          </RadioContainer>
        );
      },
      renderText: () => t('NEXT'),
    });
  }

  if (isOnlineExperiencesAllowed) {
    // Temporarily disable only for Michaels' accounts
    // const isOfflineDisabled = [MICHAELS_USER_ID, MICHAELS_CANADA_USER_ID].includes(resourceUserId);
    const isOfflineDisabled = false;

    steps.push({
      title: (
        <div>
          {t('LOCATION_OF')} <Strong>{t('EXPERIENCE_NAME_SINGULAR')}</Strong>
        </div>
      ),
      renderBodyFunc: () => {
        const radioPairProps = {
          field: { name: 'online', value: values.online || isOfflineDisabled }, // only allow online, hardcoded to be reverted later
          labels: [t('IN_PERSON'), t('ONLINE')],
          values: [false, true],
          handleToggle: handleRadioChange,
          format: 'minimal',
          isCentered: isDesktop,
          isStacked: !isDesktop,
          color: brandColor,
          disabled: [isOfflineDisabled, false], // disables In-person radio button while still displaying it, hardcoded to be reverted later
        };

        return (
          <RadioContainer>
            <RadioPair {...radioPairProps} />
          </RadioContainer>
        );
      },
      renderText: () => t('NEXT'),
    });
  }

  steps.push({
    title: (
      <div>
        {`${t('WHAT_WOULD_YOU_LIKE_TO')} `}
        <Strong>{t('NAME')}</Strong> {` ${t('THIS_CLASS')}?`}
      </div>
    ),
    renderBodyFunc: () => (
      <NameContainer>
        <TourName onChangeCallback={handleInputChange} savedValues={values} />
      </NameContainer>
    ),
    renderText: () => (creating ? `${t('CREATING')}...` : t('CREATE')),
  });

  const isMultiStep = steps.length > 1;
  const isOnLastStep = step === steps.length - 1;

  return (
    <Modal close={toggleModal} open={visible} hideClose>
      <ModalBody>
        {!isDesktop && (
          <MobileHeader>
            <MobileHeaderCenter>{`${t(
              'CREATE_NEW_CLASS'
            )} `}</MobileHeaderCenter>
          </MobileHeader>
        )}
        <Title>{steps[step].title}</Title>
        {steps[step].renderBodyFunc()}
        <Footer topBorder={isMultiStep}>
          {isMultiStep && (
            <StepIndicatorContainer>
              {[...Array(steps.length).keys()] // steps.length as array of indicies [0, 1, 2, ..., n]
                .map((num) => (
                  <StepIndicator
                    backgroundColor={
                      step === num ? Theme.colors.lightLine : 'none'
                    }
                  />
                ))}
            </StepIndicatorContainer>
          )}
          <CenterContainer>
            {(isMultiStep || !isDesktop) && (
              <BackButton step={step} onClick={moveToPreviousStep}>
                <IconContainer>
                  <Icon icon="chevron" />
                </IconContainer>
                <BackText />
              </BackButton>
            )}
            <ConfirmButton
              brandColor={brandColor}
              onClick={isOnLastStep ? handleCreateExperience : moveToNextStep}
              disabled={
                isOnLastStep ? creating || values.name.trim() === '' : false
              }
            >
              {steps[step].renderText()}
            </ConfirmButton>
          </CenterContainer>
        </Footer>
      </ModalBody>
    </Modal>
  );
};

export default NewExperienceModal;
